import classnames from 'classnames'
import { useTranslation } from 'next-i18next'
import { useState } from 'react'

import { TEST_LOCATORS } from '@src/constants/test-locators'
import { SubCollection } from '@src/pages/[...slug]/components/collection/domains/Collection'
import {
  Filter,
  SelectedFilter,
  FilterOptions,
  UpdateQueryParams,
  FilterKeys,
  EFilterKeys,
} from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import CollectionFilterLoader from '@src/pages/[...slug]/components/collection/molecules/collectionFilters/CollectionFilterLoader'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import Select, { SelectOptionProps } from '../../select'
import Skeleton from '../../skeleton/skeleton.component'
import StickyWrapper from '../../stickyWrapper'
import { B2B_SELF_FUNDED_SORT_OPTIONS, MAX_FILTERS, SORT_BY, SORT_OPTIONS } from '../constants/constants'
import Cross from '../icons/cross.inline.svg'
import Menu from '../icons/menu.inline.svg'
import FilterModal from '../modal'

import styles from './filterSection.module.scss'
import { excludeDepartmentFilter, getDisplayNameForFilterValue } from './filterUtils'

export interface FilterSectionProps {
  className?: string
  filters: Filter[]
  noOfItems: number
  selectedFilters: SelectedFilter
  sortBy: string
  showSelectedOption?: boolean
  shouldRenderColorFilterHtml?: boolean
  collectionSlug?: string
  hideFilters?: boolean
  skipSorting?: boolean
  updateQuery: (params: UpdateQueryParams) => void
  showShimmer?: boolean
  subCollectionCards?: SubCollection[]
  isB2BMode?: boolean
}

const FilterSection: React.FC<FilterSectionProps> = ({
  className,
  filters = [],
  noOfItems,
  selectedFilters,
  sortBy,
  showSelectedOption,
  shouldRenderColorFilterHtml = false,
  collectionSlug,
  hideFilters,
  skipSorting,
  updateQuery,
  showShimmer,
  isB2BMode,
}: FilterSectionProps) => {
  const { t } = useTranslation('common')
  const deviceInfo = useDeviceInfo()
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP
  const [isModalOpen, setIsModalOpen] = useState(false)
  const innerStyle = {
    // eslint-disable-next-line i18next/no-literal-string
    minHeight: SelectedFilter.isAnyFilterSelected(selectedFilters) ? 'auto' : isDesktop ? '6.8rem' : '4.9rem',
  }

  const containerStyle = {
    minHeight: isDesktop ? '6.8rem' : '4.9rem',
  }
  const _filters = excludeDepartmentFilter(filters, collectionSlug)
    .filter(item => item.values.length > 1)
    .slice(0, MAX_FILTERS)

  const renderFilter = (isIntersecting: boolean) => {
    const sendSortOpenEvent = (isOpen: boolean) => {
      if (isOpen) {
        trackEvent(TELEMETRY_EVENT_NAMES.SORT_OPENED, {})
      }
    }
    const noOfFiltersApplied =
      SelectedFilter.getSelectedValuesAsList(selectedFilters).length +
      (sortBy && sortBy !== SORT_BY.FEATURED.value ? 1 : 0)

    return (
      <>
        <div
          className={classnames(styles.filterSection, className, {
            [`${styles.filterSection__hideBorderTop}`]: !isIntersecting,
            [`${styles.hideBorderTop}`]: isB2BMode,
          })}
          style={innerStyle}
          // id="collectionFilterSection"
        >
          <div className={styles.container} style={containerStyle}>
            <div className={styles.container__wrapper}>
              <div className={styles.container__wrapper__left}>
                {!hideFilters && (
                  <>
                    <Menu
                      width={24}
                      onClick={() => {
                        setIsModalOpen(true)
                        trackEvent(TELEMETRY_EVENT_NAMES.FILTER_OPENED, {
                          // eslint-disable-next-line i18next/no-literal-string
                          filter_key: 'global',
                        })
                      }}
                      className={styles.container__wrapper__left__sign}
                    />
                    {noOfFiltersApplied > 0 && (
                      <div className={styles.container__wrapper__left__count}>
                        {'(' + noOfFiltersApplied.toString() + ')'}
                      </div>
                    )}
                    {_filters
                      .filter(x => x.values.length > 0)
                      .map(filter => {
                        return (
                          <Select
                            key={filter.name}
                            id={filter.name}
                            placeholder={filter.config.label}
                            options={getOptions(
                              filter.values,
                              filter.config.label,
                              filter.name,
                              shouldRenderColorFilterHtml,
                              collectionSlug ?? '',
                            )}
                            hiddenOptionContainer={shouldRenderColorFilterHtml}
                            showMultipleColumns={false}
                            className={styles.container__wrapper__left__filter}
                            OnOptionClick={(_e, optionValue) => {
                              updateQuery({
                                filter: {
                                  // @i18n-ignore
                                  type: 'toggle',
                                  name: filter.name,
                                  value: optionValue,
                                  label: filter.config.label,
                                },
                              })
                            }}
                            onTriggerClick={isOpen => {
                              if (isOpen) {
                                trackEvent(TELEMETRY_EVENT_NAMES.FILTER_OPENED, {
                                  filter_key: filter.name,
                                })
                              }
                            }}
                            selectedOptions={SelectedFilter.getSelectedFilterValuesByFilterName(
                              selectedFilters,
                              filter.name,
                            )}
                          />
                        )
                      })}
                  </>
                )}
              </div>
              <div className={styles.container__wrapper__right}>
                {showShimmer ? (
                  <Skeleton height={18} width={80} />
                ) : (
                  <div
                    data-testid={TEST_LOCATORS.COLLECTION_PRODUCT_COUNT}
                    className={styles.container__wrapper__right__items}
                  >
                    {noOfItems} {t('items')}
                  </div>
                )}
                {!hideFilters && !skipSorting && (
                  <Select
                    id="Sort"
                    selectedOptions={[sortBy]}
                    className={styles.container__wrapper__right__sort}
                    placeholder={t('sort')}
                    options={isB2BMode ? B2B_SELF_FUNDED_SORT_OPTIONS : SORT_OPTIONS}
                    OnOptionClick={(_e, value) => {
                      updateQuery({
                        sort: {
                          // @i18n-ignore
                          type: 'toggle',
                          value,
                        },
                      })
                    }}
                    showSelectedOptionOnDropDown={showSelectedOption}
                    showSelectedOptionOnLabel={showSelectedOption}
                    sendSortOpenEvent={sendSortOpenEvent}
                    isSortOption
                  />
                )}
              </div>
            </div>
          </div>
          {SelectedFilter.isAnyFilterSelected(selectedFilters) && (
            <div className={styles.container__filters} data-testid={TEST_LOCATORS.FILTER_SECTION_APPLIED_FILTERS}>
              {Object.entries(selectedFilters).flatMap(([filterName, filterValues]) => {
                if (!filterValues) return []
                return filterValues.map((filterValueItem: FilterKeys) => (
                  <div
                    key={`${filterName}-${filterValueItem}`}
                    data-applied-value={filterValueItem}
                    className={styles.container__filters__item}
                  >
                    <div className={styles.container__filters__item__text}>
                      {(() => {
                        // Handle Product Type filter mapping (same logic as useEnhancedFilterState):
                        // - groupA: filterName = "subcollections" → server filter = "subcollections"
                        // - groupB: filterName = "subcollections" → server filter = "productType" (needs mapping)
                        // - normal/control: filterName = "product_type" → server filter = "product_type"
                        const actualFilterName =
                          filterName === EFilterKeys.SUBCOLLECTIONS
                            ? filters.some(f => f.name === EFilterKeys.SUBCOLLECTIONS)
                              ? EFilterKeys.SUBCOLLECTIONS
                              : EFilterKeys.PRODUCT_TYPE
                            : filterName

                        return getDisplayNameForFilterValue({
                          filters,
                          filterName: actualFilterName,
                          filterValue: filterValueItem,
                        })
                      })()}
                    </div>
                    <Cross
                      // eslint-disable-next-line i18next/no-literal-string
                      data-icon="cross"
                      className={styles.container__filters__item__sign}
                      onClick={() => {
                        updateQuery({
                          filter: {
                            // @i18n-ignore
                            type: 'remove',
                            name: filterName,
                            value: filterValueItem,
                          },
                        })
                      }}
                    />
                  </div>
                ))
              })}
              {SelectedFilter.getSelectedValuesAsList(selectedFilters).length > 0 && (
                <div
                  className={styles.container__filters__clear}
                  data-testid={TEST_LOCATORS.FILTER_SECTION_CLEAR_ALL}
                  onClick={() => {
                    updateQuery({
                      filter: {
                        // @i18n-ignore
                        type: 'reset',
                      },
                      sort: {
                        // @i18n-ignore
                        type: 'reset',
                      },
                    })
                  }}
                >
                  <div className={styles.container__filters__clear__text}>{t('clear_all')}</div>
                  <Cross className={styles.container__filters__clear__sign} />
                </div>
              )}
            </div>
          )}
        </div>
        <FilterModal
          open={isModalOpen}
          filters={filters}
          noOfItems={noOfItems}
          onCancel={() => {
            setIsModalOpen(false)
          }}
          selectedFilters={selectedFilters}
          updateQuery={updateQuery}
          showSelectedOptionOnDropDown={showSelectedOption}
          skipSorting={skipSorting}
          sortBy={sortBy}
        />
      </>
    )
  }

  if (showShimmer) {
    return <CollectionFilterLoader />
  }

  return (
    <div id={'stickyWrapperNew'}>
      <StickyWrapper renderChildren={renderFilter} stickyWrapperClassName={isModalOpen ? styles.stickyWrapper : ''} />
    </div>
  )
}

const getOptions = (
  options: (string | FilterOptions)[],
  filterLabel: string,
  filterName: string,
  shouldRenderColorFilterHtml: boolean | undefined,
  collectionSlug: string,
) => {
  // TODO: @tech-debt 1. prevent filters fetch on filter/sortBy updates 2. Make SAND team provide consistent colors data
  const formattedOptions = options.map(Filter.makeSelectFilterOptionFromValue.bind(null))
  if (filterName === 'colors') {
    formattedOptions.sort((a, b) => a.value.localeCompare(b.value))
  }
  return formattedOptions.map((selectOptionProp: SelectOptionProps) => {
    return shouldRenderColorFilterHtml && filterName === 'colors'
      ? {
          ...selectOptionProp,
          isLink: true,
          linkPath: `${collectionSlug}/color/${selectOptionProp.value.toLowerCase()}`,
        }
      : selectOptionProp
  })
}

export default FilterSection
